// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useState } from "react";
import { css } from "styled-components";

type OwnProps = WidgetProps & HasProduct;

export interface ProductDetailsDocumentsStyles {
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    showMoreLink?: LinkPresentationProps;
    showLessLink?: LinkPresentationProps;
}

export const documentsStyles: ProductDetailsDocumentsStyles = {
    wrapper: {
        css: css`
            padding: 10px 15px 30px;
        `,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 10px;
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
        `,
    },
};

const styles = documentsStyles;

const ProductDetailsDocuments: React.FC<OwnProps> = ({ product: { documents } }) => {
    // xng-change
    const [showMoreIsClicked, setShowMoreIsClicked] = useState(false);
    const [documentsToShow, setDocumentsToShow] = useState(documents?.length || 0);
    // XNG-Change
    React.useEffect(() => {
        const docLength = documents?.length || 0;

        if (docLength > 3) {
            setDocumentsToShow(showMoreIsClicked ? docLength : 3);
        } else {
            setDocumentsToShow(docLength);
        }
    }, [showMoreIsClicked, documents]);

    if (!documents || documents.length === 0) {
        return null;
    }

    // XNG-Changes: Mapping of document types to sort priorities
    const documentPriorityMap: Record<string, number> = {
        "Specification Sheet": 1,
        "Submittal Sheet": 2,
        "Safety Data Sheet": 3,
        Drawing: 4,
        Manual: 5,
        Flyer: 6,
        Catalog: 7,
        Warranty: 8,
    };
    const filteredDocuments = React.useMemo(() => {
        return documents.filter(doc => doc.documentType?.toLowerCase() !== "video");
    }, [documents]);

    const sortedDocuments = React.useMemo(() => {
        return [...documents]
            .filter(doc => doc.documentType?.toLowerCase() !== "video")
            .sort((a, b) => {
                const docA = documentPriorityMap[a.documentType];
                const docB = documentPriorityMap[b.documentType];
                // If both have known priority => sort by priority
                if (docA !== undefined && docB !== undefined) {
                    if (docA === docB) {
                        return a.name.localeCompare(b.name);
                    }
                    return docA - docB;
                }

                // If only doc A has known type => it goes before doc B
                if (docA !== undefined) {
                    return -1;
                }
                // If only doc B has known type => it goes before doc A
                if (docB !== undefined) {
                    return 1;
                }

                // if neither documents are in the priority map => sort by name alphabetically
                return a.name.localeCompare(b.name);
            });
    }, [filteredDocuments]);

    // XNG-Change: add load more, less link
    const linkStyles = showMoreIsClicked ? styles.showLessLink : styles.showMoreLink;

    return (
        <StyledWrapper {...styles.wrapper}>
            {sortedDocuments.slice(0, documentsToShow).map(document => {
                return (
                    <StyledWrapper key={document.id.toString()} {...styles.documentGridItem}>
                        <Link
                            href={document.filePath}
                            target="_new"
                            {...styles.documentLink}
                            data-test-selector={`productDetails_${document.id}`}
                        >
                            {document.name}
                        </Link>
                    </StyledWrapper>
                );
            })}
            {filteredDocuments.length > 3 && (
                <StyledWrapper>
                    <Link {...linkStyles} onClick={() => setShowMoreIsClicked(!showMoreIsClicked)}>
                        {showMoreIsClicked ? translate("Show Less Documents") : translate("Load More Documents")}
                    </Link>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsDocuments),
    definition: {
        displayName: "Documents",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
